// Keyboard shortcuts: one registry, one keydown listener, one sheet.
//
// Promise:
// - Screens and parts register what a key does (`useShortcuts(defs)` or
//   `ScreenPart.shortcuts`); the registry is the only place the keys are written,
//   and the sheet (`?`) lists exactly what is registered and enabled now.
// - Keys: "c", "shift+s", "mod+k" (⌘ on mac, Ctrl elsewhere), "esc", and
//   sequences "g c" (within 1.5 s).
// - While typing (input, textarea, contenteditable) only mod combinations work,
//   and not those marked `notTyping`.
//   Held-down repeats and IME composition never trigger anything.
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from "react";

export type ShortcutGroup = "general" | "navigate" | "create" | "view";

/**
 * What a shortcut may use when it runs. Parts register their shortcuts before
 * the router exists, so the app hands them the way to move instead.
 */
export interface ShortcutRunContext {
  /** Opens a path of the app (e.g. "/me/cases"). */
  navigate(to: string): void;
}

export interface ShortcutDef {
  id: string;
  key: string;
  label(): string;
  group: ShortcutGroup;
  /** false: does nothing and is not listed. */
  when?(): boolean;
  /** true: not while typing in a field (the field has its own undo and the like). */
  notTyping?: boolean;
  run(context: ShortcutRunContext): void;
}

const SEQUENCE_MS = 1500;

export const isMac = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);

interface Combo {
  key: string;
  mod: boolean;
  shift: boolean;
  alt: boolean;
}

export function parseCombo(text: string): Combo {
  const parts = text.toLowerCase().split("+");
  const key = parts.pop() ?? "";
  return {
    key: key === "esc" ? "escape" : key,
    mod: parts.includes("mod"),
    shift: parts.includes("shift"),
    alt: parts.includes("alt"),
  };
}

function matches(combo: Combo, e: KeyboardEvent): boolean {
  const mod = isMac() ? e.metaKey : e.ctrlKey;
  // "?" is shift+/ on most layouts: compare the produced character for symbols.
  const key = e.key.toLowerCase();
  const symbol = !/^[a-z0-9]$/.test(combo.key) && combo.key.length === 1;
  return (
    key === combo.key &&
    mod === combo.mod &&
    e.altKey === combo.alt &&
    (symbol || e.shiftKey === combo.shift)
  );
}

const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  return !!el.closest(
    "input, textarea, select, [contenteditable=''], [contenteditable='true']",
  );
};

const NO_CONTEXT: ShortcutRunContext = { navigate: () => {} };

export class ShortcutStore {
  private entries = new Map<number, readonly ShortcutDef[]>();
  private next = 1;
  private subs = new Set<() => void>();
  private snapshot: readonly ShortcutDef[] = [];
  private pending: { first: string; at: number } | null = null;

  constructor(private readonly context: ShortcutRunContext = NO_CONTEXT) {}

  register(defs: readonly ShortcutDef[]): () => void {
    const token = this.next++;
    this.entries.set(token, defs);
    this.changed();
    return () => {
      this.entries.delete(token);
      this.changed();
    };
  }

  subscribe = (fn: () => void) => {
    this.subs.add(fn);
    return () => this.subs.delete(fn);
  };

  /** Every registered shortcut that is enabled now (later registrations win on the same id). */
  list = (): readonly ShortcutDef[] => this.snapshot;

  private changed() {
    const byId = new Map<string, ShortcutDef>();
    for (const defs of this.entries.values())
      for (const d of defs) byId.set(d.id, d);
    this.snapshot = [...byId.values()];
    for (const fn of this.subs) fn();
  }

  /** Handles one keydown. Returns true when a shortcut ran. */
  handle(e: KeyboardEvent, now = Date.now()): boolean {
    if (e.repeat || e.isComposing) return false;
    const typing = isTyping(e.target);
    const enabled = this.snapshot.filter((d) => d.when?.() ?? true);
    const pending = this.pending;
    this.pending = null;
    if (pending && now - pending.at <= SEQUENCE_MS && !typing) {
      for (const d of enabled) {
        const [first, second] = d.key.split(" ");
        if (second && first === pending.first && matches(parseCombo(second), e))
          return this.run(d, e);
      }
    }
    for (const d of enabled) {
      const [first, second] = d.key.split(" ");
      const combo = parseCombo(first);
      if (typing && (!combo.mod || d.notTyping)) continue;
      if (!matches(combo, e)) continue;
      if (second) {
        this.pending = { first, at: now };
        e.preventDefault();
        return false;
      }
      return this.run(d, e);
    }
    return false;
  }

  private run(d: ShortcutDef, e: KeyboardEvent) {
    e.preventDefault();
    d.run(this.context);
    return true;
  }
}

const ShortcutContext = createContext<ShortcutStore | null>(null);

export function ShortcutProvider(props: {
  store: ShortcutStore;
  children: ReactNode;
}) {
  const { store } = props;
  // frontend-development: not a fetch; one document listener for every shortcut.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      store.handle(e);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [store]);
  return (
    <ShortcutContext.Provider value={store}>
      {props.children}
    </ShortcutContext.Provider>
  );
}

export function useShortcutStore(): ShortcutStore {
  const store = useContext(ShortcutContext);
  if (!store) throw new Error("useShortcutStore outside ShortcutProvider");
  return store;
}

/** Registers shortcuts while the calling component is mounted. */
export function useShortcuts(defs: readonly ShortcutDef[]): void {
  const store = useShortcutStore();
  const latest = useRef(defs);
  latest.current = defs;
  const ids = defs.map((d) => `${d.id}:${d.key}`).join("|");
  // frontend-development: not a fetch; keeps the registry in step with the screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ids names the set; run/when are read through the ref
  useEffect(
    () =>
      store.register(
        latest.current.map((d) => ({
          ...d,
          when: () =>
            latest.current.find((x) => x.id === d.id)?.when?.() ?? true,
          run: (context) =>
            latest.current.find((x) => x.id === d.id)?.run(context),
        })),
      ),
    [store, ids],
  );
}

export function useShortcutList(): readonly ShortcutDef[] {
  const store = useShortcutStore();
  return useSyncExternalStore(store.subscribe, store.list, store.list);
}

/** "mod+k" → "⌘K" on mac, "Ctrl+K" elsewhere; "g c" → "G then C". */
export function keyLabel(key: string): string[] {
  return key.split(" ").map((step) => {
    const c = parseCombo(step);
    const name =
      c.key === "escape"
        ? "Esc"
        : c.key.length === 1
          ? c.key.toUpperCase()
          : c.key;
    const mod = c.mod ? (isMac() ? "⌘" : "Ctrl+") : "";
    const shift = c.shift ? (isMac() ? "⇧" : "Shift+") : "";
    return `${mod}${shift}${name}`;
  });
}
